import React, { useState, useMemo, useEffect } from 'react';
import './TradeParameters.css';
import { quoteService, QuoteResponse } from '../../../services/api/quoteService';
import { orderService, OrderResponse, OrderValidationResponse } from '../../../services/api/orderService';
import { useSession } from '../../../components/session/SessionManager';
import { useNotifications } from '../../../components/notifications';

// Product-specific parameter components
import { RiseFallParams } from './products/RiseFallParams';
import { HigherLowerParams } from './products/HigherLowerParams';
import { MatchesDiffersParams } from './products/MatchesDiffersParams';
import { EvenOddParams } from './products/EvenOddParams';
import { OverUnderParams } from './products/OverUnderParams';
import { AccumulatorParams } from './products/AccumulatorParams';

interface TradeParametersProps {
  selectedProduct: string;
  selectedInstrument: string;
  onTradeSuccess?: (() => void) | null;
}

const PRODUCT_NAMES: Record<string, string> = {
  'rise_fall': 'Rise/Fall Trade',
  'higher_lower': 'Higher/Lower Trade',
  'matches_differs': 'Matches/Differs Trade',
  'even_odd': 'Even/Odd Trade',
  'over_under': 'Over/Under Trade',
  'accumulator': 'Accumulator Trade'
};

const TradeParameters: React.FC<TradeParametersProps> = ({
  selectedProduct,
  selectedInstrument,
  onTradeSuccess
}) => {
  // Session and account state
  const { primaryAccountId } = useSession();
  const { showSuccess } = useNotifications();

  // Common state
  const [stake, setStake] = useState(50);

  // Live quote state
  const [quote, setQuote] = useState<QuoteResponse | null>(null);
  const [quoteValidation, setQuoteValidation] = useState<any>(null); // From quoteService
  const [orderValidation, setOrderValidation] = useState<OrderValidationResponse | null>(null); // From orderService
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [lastQuoteTime, setLastQuoteTime] = useState<number>(0);

  // Trade execution state
  const [isProcessingOrder, setIsProcessingOrder] = useState(false);
  const [orderError, setOrderError] = useState<string | null>(null);
  const [lastOrder, setLastOrder] = useState<OrderResponse | null>(null);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  
  // Rise/Fall state
  const [riseFallDirection, setRiseFallDirection] = useState<'rise' | 'fall'>('rise');
  const [riseFallDuration, setRiseFallDuration] = useState('15 minutes');
  const [allowEquals, setAllowEquals] = useState(false);
  
  // Higher/Lower state
  const [higherLowerDirection, setHigherLowerDirection] = useState<'higher' | 'lower'>('higher');
  const [barrier, setBarrier] = useState('1.08500');
  const [higherLowerDuration, setHigherLowerDuration] = useState('1 hour');
  
  // Matches/Differs state
  const [matchesDiffersDirection, setMatchesDiffersDirection] = useState<'matches' | 'differs'>('matches');
  const [selectedDigit, setSelectedDigit] = useState(5);
  const [tickCount, setTickCount] = useState(5);
  
  // Even/Odd state
  const [evenOddDirection, setEvenOddDirection] = useState<'even' | 'odd'>('even');
  const [evenOddTickCount, setEvenOddTickCount] = useState(5);
  
  // Over/Under state
  const [overUnderDirection, setOverUnderDirection] = useState<'over' | 'under'>('over');
  const [thresholdDigit, setThresholdDigit] = useState(5);
  const [overUnderTickCount, setOverUnderTickCount] = useState(5);
  
  // Accumulator state
  const [growthRate, setGrowthRate] = useState(2);
  const [takeProfitEnabled, setTakeProfitEnabled] = useState(false);
  const [takeProfitAmount, setTakeProfitAmount] = useState(100);

  const productName = PRODUCT_NAMES[selectedProduct] || PRODUCT_NAMES['rise_fall'];

  // Live quote effect
  useEffect(() => {
    // Block API calls if no instrument is selected
    if (!selectedInstrument) {
      setQuote(null);
      setQuoteValidation(null);
      setOrderValidation(null);
      setQuoteLoading(false);
      setQuoteError('Please select an instrument to get quotes');
      return;
    }

    let cancelled = false;
    setQuoteLoading(true);
    setQuoteError(null);

    // Gather parameters for quote request
    let product_params: Record<string, any> = {};

    // Add product-specific parameters (API expects product_params, not parameters)
    switch (selectedProduct) {
      case 'rise_fall':
        product_params.contract_type = riseFallDirection;
        product_params.allow_equals = allowEquals;
        product_params.maturity_type = 'duration';
        product_params.duration_unit = 'minutes';
        product_params.duration_value = parseInt(riseFallDuration) || 15;
        break;
      case 'higher_lower':
        product_params.direction = higherLowerDirection;
        product_params.barrier = barrier;
        product_params.maturity_type = 'duration';
        product_params.duration_unit = 'minutes';
        product_params.duration_value = parseInt(higherLowerDuration) || 60;
        break;
      case 'matches_differs':
        product_params.direction = matchesDiffersDirection;
        product_params.digit_prediction = selectedDigit;
        product_params.tick_count = tickCount;
        break;
      case 'even_odd':
        product_params.direction = evenOddDirection;
        product_params.tick_count = evenOddTickCount;
        break;
      case 'over_under':
        product_params.direction = overUnderDirection;
        product_params.digit_prediction = thresholdDigit;
        product_params.tick_count = overUnderTickCount;
        break;
      case 'accumulator':
        product_params.growth_rate = growthRate;
        if (takeProfitEnabled) product_params.take_profit = takeProfitAmount;
        product_params.max_ticks = 250;
        break;
      default:
        break;
    }

    // Debounce quote requests (300ms)
    const timeout = setTimeout(async () => {
      const accountId = window?.sessionStorage?.getItem('primaryAccountId') || 'acc_123456';
      const baseReq = {
        instrumentId: selectedInstrument,
        product: selectedProduct,
        stake,
        currency: 'USD',
        product_params: quoteService.formatProductParams(selectedProduct, product_params)
      };
      
      try {
        // Use getQuoteWithValidation for better UX - parallel quote + validation
        const { quote, validation } = await quoteService.getQuoteWithValidation({
          ...baseReq,
          accountId
        });
        
        if (!cancelled) {
          setQuote(quote);
          setQuoteValidation(validation);
          setQuoteLoading(false);
          setQuoteError(null);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('[TradeParameters] Quote with validation failed:', error);
          // Fallback to quote-only if validation fails
          try {
            const quote = await quoteService.getQuote(baseReq);
            if (!cancelled) {
              setQuote(quote);
              setQuoteValidation(null);
              setQuoteLoading(false);
              setQuoteError(null);
            }
          } catch (fallbackError) {
            if (!cancelled) {
              setQuote(null);
              setQuoteValidation(null);
              setQuoteLoading(false);
              setQuoteError('Failed to fetch live quote. Using fallback calculation.');
            }
          }
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [
    selectedProduct,
    selectedInstrument,
    stake,
    riseFallDirection,
    riseFallDuration,
    allowEquals,
    higherLowerDirection,
    barrier,
    higherLowerDuration,
    matchesDiffersDirection,
    selectedDigit,
    tickCount,
    evenOddDirection,
    evenOddTickCount,
    overUnderDirection,
    thresholdDigit,
    overUnderTickCount,
    growthRate,
    takeProfitEnabled,
    takeProfitAmount
  ]);

  // Continuous order validation effect (BUG-012 requirement)
  useEffect(() => {
    if (!selectedInstrument || !primaryAccountId || quoteLoading) {
      return;
    }

    let cancelled = false;
    
    // Validate order parameters every 5 seconds for continuous validation
    const validateOrder = async () => {
      try {
        const orderRequest = orderService.createOrderRequest({
          accountId: primaryAccountId,
          instrumentId: selectedInstrument,
          product: selectedProduct,
          stake,
          currency: 'USD',
          productParams: createCurrentProductParams()
        });

        const validationResult = await orderService.validateOrder(orderRequest);
        
        if (!cancelled) {
          setOrderValidation(validationResult);
        }
      } catch (error) {
        if (!cancelled) {
          // Don't show validation errors to user unless they try to place order
        }
      }
    };

    // Initial validation
    validateOrder();

    // Set up continuous validation every 5 seconds
    const validationInterval = setInterval(validateOrder, 5000);

    return () => {
      cancelled = true;
      clearInterval(validationInterval);
    };
  }, [
    selectedInstrument,
    primaryAccountId,
    selectedProduct,
    stake,
    quoteLoading,
    // Include all product parameters for validation
    riseFallDirection,
    riseFallDuration,
    allowEquals,
    higherLowerDirection,
    barrier,
    higherLowerDuration,
    matchesDiffersDirection,
    selectedDigit,
    tickCount,
    evenOddDirection,
    evenOddTickCount,
    overUnderDirection,
    thresholdDigit,
    overUnderTickCount,
    growthRate,
    takeProfitEnabled,
    takeProfitAmount
  ]);

  // Track quote update frequency for performance monitoring
  useEffect(() => {
    if (quote) {
      const now = Date.now();
      setLastQuoteTime(now);
    }
  }, [quote, lastQuoteTime]);

  // Use live quote if available, otherwise fallback to static calculation
  const payout = useMemo(() => {
    if (quote) {
      const potentialProfit = quote.payout - quote.stake;
      const profitPercentage = quote.return_percent?.toFixed(1) || '0.0';
      
      return {
        stake: quote.stake,
        commission: quote.commission,
        potentialPayout: quote.payout,
        potentialProfit,
        profitPercentage
      };
    }
    
    // Fallback static calculation when no live quote
    const commission = stake * 0.01;
    const potentialPayout = stake * 1.9;
    const potentialProfit = potentialPayout - stake;
    const profitPercentage = ((potentialProfit / stake) * 100).toFixed(1);
    
    return {
      stake,
      commission,
      potentialPayout,
      potentialProfit,
      profitPercentage
    };
  }, [quote, stake]);

  const getCurrentDirection = () => {
    switch (selectedProduct) {
      case 'rise_fall': return riseFallDirection;
      case 'higher_lower': return higherLowerDirection;
      case 'matches_differs': return matchesDiffersDirection;
      case 'even_odd': return evenOddDirection;
      case 'over_under': return overUnderDirection;
      default: return 'rise';
    }
  };

  // Trade execution functions
  const createCurrentProductParams = (): Record<string, any> => {
    switch (selectedProduct) {
      case 'rise_fall':
        return {
          direction: riseFallDirection,
          allow_equals: allowEquals,
          maturity_type: 'duration',
          duration_unit: 'minutes',
          duration_value: parseInt(riseFallDuration) || 15
        };
      case 'higher_lower':
        return {
          direction: higherLowerDirection,
          barrier,
          maturity_type: 'duration',
          duration_unit: 'minutes',
          duration_value: parseInt(higherLowerDuration) || 60
        };
      case 'matches_differs':
        return {
          direction: matchesDiffersDirection,
          digit_prediction: selectedDigit,
          tick_count: tickCount
        };
      case 'even_odd':
        return {
          direction: evenOddDirection,
          tick_count: evenOddTickCount
        };
      case 'over_under':
        return {
          direction: overUnderDirection,
          digit_prediction: thresholdDigit,
          tick_count: overUnderTickCount
        };
      case 'accumulator':
        const params: Record<string, any> = {
          growth_rate: growthRate,
          max_ticks: 250
        };
        if (takeProfitEnabled) params.take_profit = takeProfitAmount;
        return params;
      default:
        return {};
    }
  };

  const handleBuy = async () => {
    if (!selectedInstrument) {
      setOrderError('Please select an instrument before placing a trade');
      setShowErrorDialog(true);
      return;
    }

    if (!primaryAccountId) {
      setOrderError('Please ensure you are logged in to place trades');
      setShowErrorDialog(true);
      return;
    }

    setIsProcessingOrder(true);
    setOrderError(null);

    try {
      // Create order request
      const request = orderService.createOrderRequest({
        accountId: primaryAccountId,
        instrumentId: selectedInstrument,
        product: selectedProduct,
        stake,
        currency: 'USD',
        productParams: createCurrentProductParams()
      });

      const orderResponse = await orderService.placeOrder(request);
      
      setLastOrder(orderResponse);
      setIsProcessingOrder(false);
      
      // Show success toast notification
      showSuccess(
        'Trade Placed Successfully!',
        `Your ${getInstrumentSymbol()} ${getCurrentDirection()} order (#${orderResponse.orderId}) has been placed with a stake of $${orderResponse.stake.toFixed(2)}.`
      );
      
      // Refresh positions after successful trade
      if (onTradeSuccess && typeof onTradeSuccess === 'function') {
        onTradeSuccess();
      }
      
    } catch (error: any) {
      console.error('[TradeParameters] Order placement failed:', error);
      setOrderError(error.message || 'Failed to place order');
      setIsProcessingOrder(false);
      setShowErrorDialog(true);
    }
  };

  const renderProductSpecificParams = () => {
    switch (selectedProduct) {
      case 'rise_fall':
        return (
          <RiseFallParams
            direction={riseFallDirection}
            duration={riseFallDuration}
            allowEquals={allowEquals}
            onDirectionChange={setRiseFallDirection}
            onDurationChange={setRiseFallDuration}
            onAllowEqualsChange={setAllowEquals}
          />
        );

      case 'higher_lower':
        return (
          <HigherLowerParams
            direction={higherLowerDirection}
            barrier={barrier}
            duration={higherLowerDuration}
            onDirectionChange={setHigherLowerDirection}
            onBarrierChange={setBarrier}
            onDurationChange={setHigherLowerDuration}
          />
        );

      case 'matches_differs':
        return (
          <MatchesDiffersParams
            direction={matchesDiffersDirection}
            selectedDigit={selectedDigit}
            tickCount={tickCount}
            onDirectionChange={setMatchesDiffersDirection}
            onDigitSelect={setSelectedDigit}
            onTickCountChange={setTickCount}
          />
        );

      case 'even_odd':
        return (
          <EvenOddParams
            direction={evenOddDirection}
            tickCount={evenOddTickCount}
            onDirectionChange={setEvenOddDirection}
            onTickCountChange={setEvenOddTickCount}
          />
        );

      case 'over_under':
        return (
          <OverUnderParams
            direction={overUnderDirection}
            thresholdDigit={thresholdDigit}
            tickCount={overUnderTickCount}
            onDirectionChange={setOverUnderDirection}
            onThresholdDigitSelect={setThresholdDigit}
            onTickCountChange={setOverUnderTickCount}
          />
        );

      case 'accumulator':
        return (
          <AccumulatorParams
            growthRate={growthRate}
            takeProfitEnabled={takeProfitEnabled}
            takeProfitAmount={takeProfitAmount}
            onGrowthRateChange={setGrowthRate}
            onTakeProfitEnabledChange={setTakeProfitEnabled}
            onTakeProfitAmountChange={setTakeProfitAmount}
          />
        );

      default:
        return null;
    }
  };

  // Get instrument symbol for display
  const getInstrumentSymbol = () => {
    // Return the instrument ID as-is since we no longer have static mappings
    // In a real implementation, this would fetch the symbol from the instrument service
    return selectedInstrument || 'No instrument selected';
  };

  return (
    <>
      <aside className="trade-parameters-sidebar">
        <div className="parameters-content">
          <h3>{productName}</h3>
          
          {renderProductSpecificParams()}

          <div className="parameter-group">
            <label>Stake</label>
            <input 
              type="number" 
              className="input-field" 
              min="1" 
              max="5000"
              value={stake}
              onChange={(e) => setStake(Number(e.target.value))}
            />
          </div>

          <div className="parameter-group">
            <label>Payout Calculation</label>
            <div className="payout-display">
              {quoteLoading && (
                <div className="payout-row" style={{ color: '#888' }}>
                  Loading live quote...
                </div>
              )}
              {quoteError && (
                <div className="payout-row" style={{ color: '#ff6b6b' }}>
                  {quoteError}
                </div>
              )}
              
              {/* Quote Validation Status */}
              {quoteValidation && (
                <div className="payout-row validation-status">
                  <span>Quote Status:</span>
                  <span style={{ 
                    color: quoteValidation.valid ? '#22c55e' : '#ef4444',
                    fontWeight: '600'
                  }}>
                    {quoteValidation.valid ? '✓ Valid' : '⚠ Invalid'}
                  </span>
                </div>
              )}
              
              {/* Quote Freshness Indicator */}
              {quote && lastQuoteTime > 0 && (
                <div className="payout-row quote-freshness">
                  <span>Quote Updated:</span>
                  <span style={{ 
                    color: Date.now() - lastQuoteTime < 5000 ? '#22c55e' : '#f59e0b',
                    fontSize: '12px'
                  }}>
                    {Math.floor((Date.now() - lastQuoteTime) / 1000)}s ago
                  </span>
                </div>
              )}
              
              <div className="payout-row">
                <span>Stake:</span>
                <span>${payout.stake.toFixed(2)}</span>
              </div>
              <div className="payout-row">
                <span>Commission:</span>
                <span>${payout.commission.toFixed(2)}</span>
              </div>
              <div className="payout-row">
                <span>Potential Payout:</span>
                <span>${payout.potentialPayout.toFixed(2)}</span>
              </div>
              <div className="payout-row">
                <span>Potential Profit:</span>
                <span>${payout.potentialProfit.toFixed(2)} ({payout.profitPercentage}%)</span>
              </div>
            </div>
          </div>

          {/* Order Validation Status */}
          {orderValidation && (
            <div className="parameter-group">
              <div className="validation-display">
                <div className="validation-header" style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: '14px',
                  fontWeight: '600',
                  marginBottom: '8px'
                }}>
                  <span>Order Validation</span>
                  <span style={{ 
                    color: orderValidation.valid ? '#22c55e' : '#ef4444',
                    fontWeight: '600',
                    fontSize: '14px'
                  }}>
                    {orderValidation.valid ? '✓ Ready' : '⚠ Issues'}
                  </span>
                </div>
                {orderValidation.error && (
                  <div className="validation-error" style={{
                    color: '#ef4444',
                    fontSize: '12px',
                    marginTop: '4px',
                    padding: '4px 8px',
                    background: 'rgba(239, 68, 68, 0.1)',
                    borderRadius: '4px'
                  }}>
                    {orderValidation.error}
                  </div>
                )}
                {orderValidation.valid && (
                  <div className="validation-details" style={{
                    fontSize: '12px',
                    color: '#666',
                    marginTop: '4px'
                  }}>
                    <div>Payout: ${orderValidation.payout.toFixed(2)}</div>
                    <div>Return: {orderValidation.return_percent.toFixed(1)}%</div>
                    <div>Commission: ${orderValidation.commission.toFixed(2)}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Last Order Information */}
          {lastOrder && (
            <div className="parameter-group">
              <div className="last-order-display" style={{
                background: 'rgba(34, 197, 94, 0.1)',
                border: '1px solid #22c55e',
                borderRadius: '6px',
                padding: '8px 12px',
                fontSize: '12px'
              }}>
                <div className="last-order-header" style={{
                  fontWeight: '600',
                  color: '#22c55e',
                  marginBottom: '4px'
                }}>
                  ✓ Last Trade Successful
                </div>
                <div style={{ color: '#666' }}>
                  Order #{lastOrder.orderId} • ${lastOrder.stake.toFixed(2)} stake
                </div>
                <button 
                  onClick={() => {
                    // Quick re-trade functionality
                    setStake(lastOrder.stake);
                    // Could also restore other parameters from lastOrder
                  }}
                  style={{
                    background: 'none',
                    border: '1px solid #22c55e',
                    color: '#22c55e',
                    borderRadius: '4px',
                    padding: '2px 8px',
                    fontSize: '11px',
                    marginTop: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Repeat Trade
                </button>
              </div>
            </div>
          )}

          {orderError && (
            <div className="parameter-group">
              <div className="error-message" style={{ 
                background: 'rgba(255, 107, 107, 0.1)', 
                border: '1px solid var(--error)', 
                borderRadius: '6px', 
                padding: '8px 12px', 
                color: 'var(--error)', 
                fontSize: '14px' 
              }}>
                {orderError}
              </div>
            </div>
          )}

          <button 
            className="buy-button" 
            onClick={handleBuy} 
            disabled={
              quoteLoading || 
              isProcessingOrder || 
              (orderValidation !== null && !orderValidation.valid)
            }
            style={{
              opacity: (orderValidation !== null && !orderValidation.valid) ? 0.6 : 1
            }}
          >
            {quoteLoading ? 'Loading...' : 
             isProcessingOrder ? 'Processing...' :
             (orderValidation !== null && !orderValidation.valid) ? 'Order Invalid' :
             'Buy'}
          </button>
          
          <a href="#" className="learn-link">
            Learn about {productName.replace(' Trade', '')} options
          </a>
        </div>
      </aside>

      {/* Error Dialog */}
      {showErrorDialog && (
        <div className="modal-overlay">
          <div className="error-dialog">
            <div className="error-header">
              <h3>Trade Error</h3>
              <button 
                className="close-btn" 
                onClick={() => setShowErrorDialog(false)}
              >
                ×
              </button>
            </div>
            <div className="error-content">
              <p>{orderError}</p>
            </div>
            <div className="error-actions">
              <button 
                className="error-ok-btn" 
                onClick={() => setShowErrorDialog(false)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TradeParameters;
export { TradeParameters };
